import { alpha, createTheme, responsiveFontSizes } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

/**
 * Static Ascend Platform MUI theme.
 * All theming is fixed — dynamic tenant color overrides have been removed.
 * Tenant branding is limited to the logo, handled directly in AppBar via tenantService.
 */

// Brand anchor colors — referenced by both standard MUI palette slots
// and the custom semantic tokens below. Changing these here re-skins the app.
// Contrast notes (WCAG 2.1 AA, decided 2026-08-04):
// - PRIMARY_MAIN text on white is 3.66:1 — below AA for small text. Green *text*
//   (text/outlined buttons, links, chip labels) therefore uses PRIMARY_DARK (5.19:1).
// - White on PRIMARY_MAIN (contained CTAs, filled success alerts) is also 3.66:1.
//   This is a known, accepted AA gap: the brand green stays on filled CTAs by decision.
const PRIMARY_MAIN = '#559534';
const PRIMARY_DARK = '#437a2b';
const SECONDARY_MAIN = '#cc5f20';
const SECONDARY_DARK = '#a34c18';
const SUCCESS_MAIN = '#16a34a';
const SUCCESS_DARK = '#166534';
const WARNING_MAIN = '#d97706';
const WARNING_DARK = '#b45309';
const ERROR_MAIN = '#d32f2f';
const ERROR_DARK = '#c62828';

/**
 * Module augmentation: extend MUI's Palette/PaletteOptions with semantic tokens
 * for surfaces, tints, brand chrome, and progress states. Lets pages reference
 * `theme.palette.status.inProgress.fg` instead of hard-coding `#437a2b`.
 */
declare module '@mui/material/styles' {
  interface Palette {
    surface: {
      /** Slightly tinted neutral background — auth/marketing pages */
      subtle: string;
      /** App content scroll surface (mirrors background.default) */
      sunken: string;
      /** Card / shell surface (mirrors background.paper) */
      raised: string;
    };
    tint: {
      /** ~7% primary — soft hover surface, pill bg */
      primarySoft: string;
      /** ~15% primary — selected/badge bg */
      primaryMuted: string;
      /** ~26% primary — borders, prominent overlays */
      primaryStrong: string;
      /** ~5% neutral — generic hover bg on neutral surfaces */
      neutralSoft: string;
      /** ~10% neutral — segmented control track */
      neutralMuted: string;
    };
    status: {
      enrolled: { bg: string; fg: string; dot: string };
      inProgress: { bg: string; fg: string; dot: string };
      onPace: { bg: string; fg: string; dot: string };
      completed: { bg: string; fg: string; dot: string };
      webinar: { bg: string; fg: string };
      conference: { bg: string; fg: string };
      onDemand: { bg: string; fg: string };
      modified: { bg: string; fg: string };
      atRisk: { bg: string; fg: string; dot: string };
      attention: { bg: string; fg: string; dot: string };
    };
    brand: {
      /** Dark anchor for hero / marketing surfaces (slate-900 family) */
      heroSurface: string;
      heroSurfaceMid: string;
    };
    /**
     * Sequential brand-green ramp for magnitude-sorted data breakdowns
     * (segment bars, category ledgers), dark to light. Sort values descending
     * and index with `i % categorical.length` so the largest gets the deepest
     * green.
     */
    categorical: string[];
  }

  interface PaletteOptions {
    surface?: Palette['surface'];
    tint?: Palette['tint'];
    status?: Palette['status'];
    brand?: Palette['brand'];
    categorical?: Palette['categorical'];
  }
}

export const ascendTheme: Theme = responsiveFontSizes(createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: PRIMARY_MAIN,
      light: '#4c8c2b',
      dark: PRIMARY_DARK,
      contrastText: '#ffffff',
    },
    secondary: {
      main: SECONDARY_MAIN,
      light: '#e89558',
      dark: SECONDARY_DARK,
      contrastText: '#ffffff',
    },
    success: {
      main: '#16a34a',
      light: '#4ade80',
      dark: '#15803d',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#d97706',
      light: '#fbbf24',
      dark: '#b45309',
      contrastText: '#ffffff',
    },
    info: {
      main: '#0284c7',
      light: '#38bdf8',
      dark: '#0369a1',
      contrastText: '#ffffff',
    },
    error: {
      main: '#d32f2f',
      contrastText: '#ffffff',
    },
    divider: 'rgba(0, 0, 0, 0.13)',
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.60)',
    },
    background: {
      default: '#f1f3f0',
      paper: '#fefffe',
    },
    action: {
      active: 'rgba(0, 0, 0, 0.72)',
      hover: 'rgba(86, 151, 55, 0.10)',
      selected: 'rgba(86, 151, 55, 0.22)',
      disabled: 'rgba(0, 0, 0, 0.30)',
      disabledBackground: 'rgba(0, 0, 0, 0.06)',
    },
    // ── Semantic tokens (Ascend extension) ──
    surface: {
      subtle: '#f1f4ee',
      sunken: '#f1f3f0',
      raised: '#fefffe',
    },
    tint: {
      primarySoft: alpha(PRIMARY_MAIN, 0.08),
      primaryMuted: alpha(PRIMARY_MAIN, 0.18),
      primaryStrong: alpha(PRIMARY_MAIN, 0.28),
      neutralSoft: 'rgba(0, 0, 0, 0.05)',
      neutralMuted: 'rgba(0, 0, 0, 0.10)',
    },
    status: {
      // Generic neutral — pre-engagement, no progress yet
      enrolled: {
        bg: 'rgba(0, 0, 0, 0.07)',
        fg: 'rgba(0, 0, 0, 0.65)',
        dot: 'rgba(0, 0, 0, 0.42)',
      },
      // Active learning — borrows brand identity
      inProgress: {
        bg: alpha(PRIMARY_MAIN, 0.15),
        fg: PRIMARY_DARK,
        dot: PRIMARY_MAIN,
      },
      // Compliance standing "in progress / on pace" — info-blue family,
      // deliberately NOT a green: in the standing ramp (attention → at risk →
      // on pace → compliant), green appears exactly once and means "done".
      // Brand-green inProgress next to success-green completed is
      // indistinguishable at dot/segment scale.
      onPace: {
        bg: alpha('#0284c7', 0.13),
        fg: '#0369a1',
        dot: '#0284c7',
      },
      // Compliance achieved — green family
      completed: {
        bg: alpha(SUCCESS_MAIN, 0.15),
        fg: SUCCESS_DARK,
        dot: SUCCESS_MAIN,
      },
      // Live event — orange secondary (energy, urgency)
      webinar: {
        bg: alpha(SECONDARY_MAIN, 0.15),
        fg: SECONDARY_DARK,
      },
      // Conference — distinct from webinars (blue family)
      conference: {
        bg: alpha('#0284c7', 0.16),
        fg: '#075985',
      },
      // Self-paced — success green family
      onDemand: {
        bg: alpha(SUCCESS_MAIN, 0.15),
        fg: SUCCESS_DARK,
      },
      // User-modified values (e.g. custom CPE requirements) — amber, "review carefully"
      modified: {
        bg: alpha(WARNING_MAIN, 0.14),
        fg: WARNING_DARK,
      },
      // Predictive shortfall — behind pace, period not yet urgent (warning family)
      atRisk: {
        bg: alpha(WARNING_MAIN, 0.14),
        fg: WARNING_DARK,
        // Dark amber: WARNING_MAIN is 2.9:1 on white, under the 3:1 minimum
        // for non-text indicators (dots, bar segments).
        dot: WARNING_DARK,
      },
      // Deadline urgency — error family
      attention: {
        bg: alpha(ERROR_MAIN, 0.12),
        fg: ERROR_DARK,
        dot: ERROR_MAIN,
      },
    },
    brand: {
      heroSurface: '#0c200f',     // deep forest anchor for marketing dark surfaces
      heroSurfaceMid: '#1a4328',  // mid forest for hero gradient stops
    },
    // Sequential data-viz ramp for magnitude-sorted breakdowns (segment bars,
    // category ledgers): a single brand-green family stepping dark to light,
    // hue drifting slightly toward forest as it deepens. Monochrome on purpose
    // so breakdowns can never collide with the app's semantic colors (orange =
    // webinar, blue = conference). Pair index 0 with the largest value.
    categorical: [
      'oklch(0.38 0.07 150)', // deep forest
      'oklch(0.48 0.09 147)',
      'oklch(0.57 0.10 144)', // ~growth green (brand anchor)
      'oklch(0.66 0.11 142)',
      'oklch(0.75 0.10 140)',
      'oklch(0.83 0.08 139)',
      'oklch(0.90 0.05 138)', // palest sage
    ],
  },
  typography: {
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    h1: {
      fontSize: '3rem',
      fontWeight: 800,
      lineHeight: 1.12,
      letterSpacing: '-0.03em',
    },
    h2: {
      fontSize: '2.25rem',
      fontWeight: 800,
      lineHeight: 1.15,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontSize: '1.875rem',
      fontWeight: 800,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.015em',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 700,
      lineHeight: 1.35,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontSize: '1.0625rem',
      fontWeight: 700,
      lineHeight: 1.45,
      letterSpacing: '-0.005em',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0.01em',
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.01em',
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.75,
      letterSpacing: '0.005em',
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.57,
      letterSpacing: '0.005em',
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      lineHeight: 2.66,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.66,
      letterSpacing: '0.025em',
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 700,
      lineHeight: 1.75,
      letterSpacing: '0.015em',
      textTransform: 'none',
    },
  },
  spacing: 8,
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiLink: {
      // Links default to primary.main (3.66:1 on white); step down to the
      // dark green so inline links pass AA for small text.
      variants: [
        {
          props: { color: 'primary' },
          style: {
            color: PRIMARY_DARK,
            textDecorationColor: alpha(PRIMARY_DARK, 0.4),
          },
        },
      ],
    },
    MuiButton: {
      // Green button *labels* on light surfaces need the dark step to pass AA.
      variants: [
        { props: { variant: 'text', color: 'primary' }, style: { color: PRIMARY_DARK } },
        { props: { variant: 'outlined', color: 'primary' }, style: { color: PRIMARY_DARK } },
      ],
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 700,
          letterSpacing: '0.015em',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          boxShadow: '0px 1px 4px rgba(0,0,0,0.16), 0px 1px 2px rgba(0,0,0,0.10)',
          '&:hover': {
            boxShadow: '0px 4px 12px rgba(0,0,0,0.18), 0px 2px 4px rgba(0,0,0,0.12)',
          },
        },
        outlined: {
          borderColor: 'rgba(86, 151, 55, 0.60)',
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            borderColor: 'rgba(86, 151, 55, 0.85)',
          },
        },
        sizeSmall: {
          fontSize: '0.8125rem',
          padding: '6px 14px',
        },
        sizeLarge: {
          padding: '12px 28px',
          fontSize: '0.9375rem',
        },
      },
    },
    MuiCard: {
      // Flat card contract: `variant="outlined"` is the canonical static
      // container — divider border, shape.borderRadius corners, no elevation,
      // no hover-lift. Elevated cards (the default) keep the 14px radius and
      // lift; use them only for interactive/clickable surfaces.
      variants: [
        {
          props: { variant: 'outlined' },
          style: {
            borderRadius: 8,
            boxShadow: 'none',
            transition: 'none',
            '&:hover': { boxShadow: 'none' },
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: '1px solid rgba(0, 0, 0, 0.10)',
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.06), 0px 1px 3px rgba(0, 0, 0, 0.04)',
          transition: 'box-shadow 0.25s cubic-bezier(0.16, 1, 0.3, 1), transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
          '&:hover': {
            boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.10), 0px 2px 6px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: 16,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 14,
        },
        elevation1: {
          boxShadow: '0px 1px 4px rgba(0,0,0,0.10), 0px 1px 2px rgba(0,0,0,0.06)',
        },
        elevation2: {
          boxShadow: '0px 3px 10px rgba(0,0,0,0.10), 0px 1px 4px rgba(0,0,0,0.06)',
        },
        elevation3: {
          boxShadow: '0px 6px 20px rgba(0,0,0,0.12), 0px 2px 8px rgba(0,0,0,0.08)',
        },
        elevation4: {
          boxShadow: '0px 10px 32px rgba(0,0,0,0.14), 0px 4px 12px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiChip: {
      // Primary outlined chips: dark-green label for AA, brand-green border
      // (the generic outlined grey border must not clobber color="primary").
      variants: [
        {
          props: { variant: 'outlined', color: 'primary' },
          style: { color: PRIMARY_DARK, borderColor: alpha(PRIMARY_MAIN, 0.6) },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: '0.8125rem',
          fontWeight: 600,
          lineHeight: '18px',
          letterSpacing: '0.01em',
        },
        sizeSmall: {
          height: '24px',
          fontSize: '0.75rem',
        },
        outlined: {
          borderColor: 'rgba(0, 0, 0, 0.22)',
        },
      },
    },
    MuiToggleButton: {
      // Preset/filter toggles: sentence case on a paper surface, selected via
      // the shared selection vocabulary (primary tint + dark-step text) that
      // nav and menu items use — a green-fill treatment fails AA at this size.
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '0.8125rem',
          fontWeight: 500,
          letterSpacing: '0.005em',
          color: 'rgba(0, 0, 0, 0.87)',
          backgroundColor: '#fefffe',
          borderColor: 'rgba(0, 0, 0, 0.13)',
          padding: '5px 14px',
          transition: 'background-color 150ms ease, color 150ms ease, border-color 150ms ease',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: alpha(PRIMARY_MAIN, 0.18),
            color: PRIMARY_DARK,
            fontWeight: 600,
            borderColor: alpha(PRIMARY_MAIN, 0.28),
            '&:hover': {
              backgroundColor: alpha(PRIMARY_MAIN, 0.28),
            },
          },
          '&.Mui-selected + .MuiToggleButtonGroup-grouped': {
            borderLeftColor: alpha(PRIMARY_MAIN, 0.28),
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#fefffe',
          color: 'rgba(0, 0, 0, 0.87)',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.10)',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(86, 151, 55, 0.65)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
          },
        },
        notchedOutline: {
          borderColor: 'rgba(0, 0, 0, 0.25)',
          transition: 'border-color 0.2s ease',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-selected': {
            backgroundColor: 'rgba(86, 151, 55, 0.16)',
            '&:hover': {
              backgroundColor: 'rgba(86, 151, 55, 0.22)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.13)',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          backgroundColor: '#d32f2f',
          color: '#ffffff',
          fontWeight: 600,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: PRIMARY_MAIN,
          color: '#ffffff',
          fontWeight: 600,
        },
        colorDefault: {
          backgroundColor: alpha(PRIMARY_MAIN, 0.38),
          // brand.heroSurfaceMid — 7.3:1 on the 38% tint (PRIMARY_DARK was 3.35:1)
          color: '#1a4328',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 6,
          borderRadius: 8,
          backgroundColor: 'rgba(0, 0, 0, 0.12)',
        },
        bar: {
          borderRadius: 8,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          letterSpacing: '0.01em',
          minHeight: 44,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 6,
          fontSize: '0.75rem',
          fontWeight: 600,
          backgroundColor: 'rgba(12, 32, 15, 0.92)',
          backdropFilter: 'blur(4px)',
        },
        arrow: {
          color: 'rgba(12, 32, 15, 0.92)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontSize: '0.875rem',
        },
        standardSuccess: {
          backgroundColor: 'rgba(22, 163, 74, 0.14)',
          color: '#15803d',
        },
        // Success confirmations (snackbars) carry the brand green, not the
        // Tailwind success green, so they read as part of the Ascend theme.
        filledSuccess: {
          backgroundColor: PRIMARY_MAIN,
        },
        standardWarning: {
          backgroundColor: 'rgba(217, 119, 6, 0.14)',
          color: '#b45309',
        },
        standardError: {
          backgroundColor: 'rgba(211, 47, 47, 0.14)',
          color: '#c62828',
        },
        standardInfo: {
          backgroundColor: 'rgba(2, 132, 199, 0.14)',
          color: '#0369a1',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          boxShadow: '0px 24px 48px rgba(0,0,0,0.18), 0px 8px 16px rgba(0,0,0,0.10)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.125rem',
          fontWeight: 700,
          padding: '20px 24px 12px',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          border: '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: '0px 10px 32px rgba(0,0,0,0.14), 0px 3px 8px rgba(0,0,0,0.06)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: '2px 6px',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(86, 151, 55, 0.16)',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: '#f4f6f3',
            color: 'rgba(0, 0, 0, 0.64)',
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(0, 0, 0, 0.08)',
          fontSize: '0.875rem',
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
  },
}));

// Kept for backward-compat; prefer importing ascendTheme directly.
export interface ThemeConfig {
  tenantId: string;
  companyName: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  logoUrl?: string;
  fontFamily?: string;
}

// Kept for backward-compat — no longer used for dynamic theming.
export interface AppSettings {
  appName: string;
  companyName: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  logoUrl: string;
}

/** @deprecated Use ascendTheme directly */
export function createMuiTheme(_config?: ThemeConfig | AppSettings): Theme {
  return ascendTheme;
}
